// API configuration and session management utilities
// Supports both same-domain (cookies) and cross-domain (tokens) communication

import { API_CONFIG } from '../config/api'

const API_BASE_URL = API_CONFIG.BASE_URL

// Session management
let sessionId: string | null = null
let retryCount = 0
const MAX_RETRIES = 3

export const getSessionId = (): string | null => {
  return sessionId
}

export const setSessionId = (id: string): void => {
  sessionId = id
}

export const clearSessionId = (): void => {
  sessionId = null
}

// Check if we're on the same domain as the backend
const isSameDomain = (): boolean => {
  if (typeof window === 'undefined') return false
  const currentDomain = window.location.hostname
  const backendDomain = new URL(API_BASE_URL).hostname
  const result = currentDomain === backendDomain
  console.log('isSameDomain: Current domain:', currentDomain, 'Backend domain:', backendDomain, 'Result:', result)
  return result
}

// Prepare request body with session ID for cross-domain requests
const prepareRequestBody = (data: any): any => {
  if (isSameDomain()) {
    // Same domain - no need to include session_id
    return data
  } else {
    // Cross domain - include session_id if available
    return sessionId ? { ...data, session_id: sessionId } : data
  }
}

// Prepare query parameters with session ID for cross-domain requests
const prepareQueryParams = (params: Record<string, string> = {}): string => {
  if (isSameDomain()) {
    // Same domain - no need to include session_id
    return new URLSearchParams(params).toString()
  } else {
    // Cross domain - include session_id if available
    const queryParams = { ...params }
    if (sessionId) {
      queryParams.session_id = sessionId
    }
    return new URLSearchParams(queryParams).toString()
  }
}

// Generic API request function
export const apiRequest = async (
  endpoint: string,
  options: RequestInit = {},
  includeCredentials: boolean = true
): Promise<Response> => {
  const url = `${API_BASE_URL}${endpoint}`
  
  // Check retry limit
  if (retryCount >= MAX_RETRIES) {
    console.error('apiRequest: Max retries reached, stopping requests')
    retryCount = 0 // Reset for future requests
    throw new Error('Max retries reached')
  }
  
  // For cross-domain requests, we need to handle session differently
  if (!isSameDomain() && includeCredentials) {
    // Cross-domain with credentials - use token-based approach
    const requestOptions: RequestInit = {
      ...options,
      credentials: 'omit', // Don't send cookies for cross-domain
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    }

    // If it's a POST/PUT request, include session_id in body
    if (options.body && typeof options.body === 'string') {
      try {
        const bodyData = JSON.parse(options.body)
        const updatedBody = prepareRequestBody(bodyData)
        requestOptions.body = JSON.stringify(updatedBody)
      } catch (e) {
        // If body is not JSON, keep as is
      }
    } else if (options.body && options.body instanceof FormData) {
      // For FormData, we can't easily add session_id, so we'll use query params
      const queryString = prepareQueryParams()
      const separator = endpoint.includes('?') ? '&' : '?'
      const finalUrl = queryString ? `${url}${separator}${queryString}` : url
      return fetch(finalUrl, requestOptions)
    } else if (options.method === 'POST' && !options.body && sessionId) {
      // For POST requests without body, add session_id in body
      requestOptions.body = JSON.stringify({ session_id: sessionId })
      console.log('apiRequest: Added session_id to empty POST body:', sessionId)
    } else if (options.method === 'POST' && !options.body && !sessionId) {
      console.log('apiRequest: POST request without body and no session ID available')
    }

    try {
      const response = await fetch(url, requestOptions)
      
      // If request failed, increment retry count
      if (!response.ok) {
        retryCount++
        console.warn(`apiRequest: Request failed (${response.status}), retry ${retryCount}/${MAX_RETRIES}`)
      } else {
        // Reset retry count on success
        retryCount = 0
      }
      
      return response
    } catch (error) {
      retryCount++
      console.error(`apiRequest: Network error, retry ${retryCount}/${MAX_RETRIES}:`, error)
      throw error
    }
  } else {
    // Same domain or no credentials needed - use cookie-based approach
    try {
      const response = await fetch(url, {
        ...options,
        credentials: includeCredentials ? 'include' : 'omit',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      })
      
      // If request failed, increment retry count
      if (!response.ok) {
        retryCount++
        console.warn(`apiRequest: Request failed (${response.status}), retry ${retryCount}/${MAX_RETRIES}`)
      } else {
        // Reset retry count on success
        retryCount = 0
      }
      
      return response
    } catch (error) {
      retryCount++
      console.error(`apiRequest: Network error, retry ${retryCount}/${MAX_RETRIES}:`, error)
      throw error
    }
  }
}

// Extract session ID from response if present
export const extractSessionId = async (response: Response): Promise<void> => {
  if (!isSameDomain()) {
    // Only extract session_id for cross-domain requests
    let sessionIdFound = null
    
    // First try to get from custom header
    const responseData = response.headers.get(API_CONFIG.SESSION.SESSION_HEADER)
    console.log('extractSessionId: Response header', API_CONFIG.SESSION.SESSION_HEADER, ':', responseData)
    
    if (responseData) {
      sessionIdFound = responseData
    } else {
      // If no custom header, try to extract from set-cookie header
      const setCookieHeader = response.headers.get('set-cookie')
      console.log('extractSessionId: Set-Cookie header:', setCookieHeader)
      
      if (setCookieHeader) {
        // Parse the session cookie
        const sessionMatch = setCookieHeader.match(/session=([^;]+)/)
        if (sessionMatch) {
          sessionIdFound = sessionMatch[1]
          console.log('extractSessionId: Extracted session ID from cookie:', sessionIdFound)
        }
      }
      
      // If still no session ID, try to extract from response body
      if (!sessionIdFound) {
        try {
          // Clone the response to read the body (since it can only be read once)
          const responseClone = response.clone()
          const responseBody = await responseClone.json()
          console.log('extractSessionId: Response body:', responseBody)
          
          if (responseBody && responseBody.session_id) {
            sessionIdFound = responseBody.session_id
            console.log('extractSessionId: Extracted session ID from response body:', sessionIdFound)
          }
        } catch (error) {
          console.log('extractSessionId: Could not parse response body:', error)
        }
      }
    }
    
    if (sessionIdFound) {
      setSessionId(sessionIdFound)
      console.log('extractSessionId: Session ID set to:', sessionIdFound)
    } else {
      console.log('extractSessionId: No session ID found in headers, cookies, or response body')
    }
  } else {
    console.log('extractSessionId: Same domain, not extracting session ID')
  }
}

// Chat API functions
export const chatAPI = {
  // Send a chat message
  sendMessage: async (message: string): Promise<Response> => {
    const response = await apiRequest(API_CONFIG.ENDPOINTS.CHAT, {
      method: 'POST',
      body: JSON.stringify({ message }),
    })
    
    // Extract session ID from response if present
    await extractSessionId(response)
    
    return response
  },

  // Get chat history
  getHistory: async (): Promise<Response> => {
    const response = await apiRequest(API_CONFIG.ENDPOINTS.CHAT_HISTORY, {
      method: 'GET',
    })
    
    await extractSessionId(response)
    return response
  },

  // End chat session and send transcript
  endChat: async (): Promise<Response> => {
    // Debug: Log current session ID before making request
    console.log('endChat: Current session ID:', getSessionId())
    console.log('endChat: Is cross-domain:', !isSameDomain())
    
    const response = await apiRequest(API_CONFIG.ENDPOINTS.CHAT_END, {
      method: 'POST',
    })
    
    await extractSessionId(response)
    return response
  },

  // Clear chat history
  clearChat: async (): Promise<Response> => {
    // Debug: Log current session ID before making request
    console.log('clearChat: Current session ID:', getSessionId())
    console.log('clearChat: Is cross-domain:', !isSameDomain())
    
    const response = await apiRequest(API_CONFIG.ENDPOINTS.CHAT_CLEAR, {
      method: 'POST',
    })
    
    await extractSessionId(response)
    
    return response
  },
}

// Contact API functions
export const contactAPI = {
  sendContact: async (formData: any): Promise<Response> => {
    const response = await apiRequest(API_CONFIG.ENDPOINTS.CONTACT, {
      method: 'POST',
      body: JSON.stringify(formData),
    })
    
    await extractSessionId(response)
    return response
  },
}
